/**
 * RangeInput memorisation: inject a lock icon next to every .RangeInput (Ant Design RangePicker).
 * On lock click: save start/end date to localStorage.
 * When a .RangeInput is encountered: if saved dates exist, populate the inputs.
 */
const RANGE_INPUT_MEMORY_KEY = 'lux_rangeInput_memory'
const LOCK_MARKER_ATTR = 'data-lux-range-memory-injected'

var LUX_RANGE_FONT_STYLE = [
  'font-family:"IBM Plex Sans",sans-serif',
  'font-feature-settings:normal',
  'font-kerning:auto',
  'font-language-override:normal',
  'font-optical-sizing:auto',
  'font-size:16px',
  'font-size-adjust:none',
  'font-stretch:100%',
  'font-style:normal',
  'font-variant-alternates:normal',
  'font-variant-caps:normal',
  'font-variant-east-asian:normal',
  'font-variant-emoji:normal',
  'font-variant-ligatures:normal',
  'font-variant-numeric:normal',
  'font-variant-position:normal',
  'font-variation-settings:normal',
  'font-weight:500',
].join(';')

function getRangeInputs(rangeEl) {
  // Prefer Ant Design rc-picker cells so we do not grab hidden/extra inputs first.
  var fromCells = rangeEl.querySelectorAll('.ant-picker-input input')
  if (fromCells.length >= 2) {
    return { startInput: fromCells[0], endInput: fromCells[1] }
  }
  var list = Array.prototype.slice.call(rangeEl.querySelectorAll('input')).filter(function (inp) {
    return inp.type !== 'hidden'
  })
  if (list.length >= 2) {
    return { startInput: list[0], endInput: list[1] }
  }
  return null
}

function getStoredRange() {
  try {
    const raw = localStorage.getItem(RANGE_INPUT_MEMORY_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed.startDate === 'string' && typeof parsed.endDate === 'string') {
      return { startDate: parsed.startDate, endDate: parsed.endDate }
    }
  } catch (e) {
    // ignore
  }
  return null
}

function setStoredRange(startDate, endDate) {
  localStorage.setItem(
    RANGE_INPUT_MEMORY_KEY,
    JSON.stringify({ startDate: String(startDate), endDate: String(endDate) })
  )
}

function clearStoredRange() {
  localStorage.removeItem(RANGE_INPUT_MEMORY_KEY)
}

function setInputValue(input, value) {
  if (!input) return
  const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')
  if (descriptor && descriptor.set) {
    descriptor.set.call(input, value)
    input.dispatchEvent(new Event('input', { bubbles: true }))
    input.dispatchEvent(new Event('change', { bubbles: true }))
  } else {
    input.value = value
    input.dispatchEvent(new Event('input', { bubbles: true }))
    input.dispatchEvent(new Event('change', { bubbles: true }))
  }
}

function applyStoredRangeToElement(rangeEl) {
  const stored = getStoredRange()
  if (!stored) return
  const inputs = getRangeInputs(rangeEl)
  if (!inputs) return
  setInputValue(inputs.startInput, stored.startDate)
  setInputValue(inputs.endInput, stored.endDate)
}

/**
 * React/Ant often re-renders after our first write and clears only the start field.
 * Re-apply a few times while the node is connected; also repair on focus. Reads getStoredRange()
 * each time so unlock clears behavior (no stale closure).
 */
function scheduleStoredRangeRepair(rangeEl) {
  if (!rangeEl) return

  function repairIfNeeded() {
    if (!rangeEl.isConnected) return
    var stored = getStoredRange()
    if (!stored) return
    var expectStart = String(stored.startDate || '').trim()
    var expectEnd = String(stored.endDate || '').trim()
    if (!expectStart || !expectEnd) return
    var inputs = getRangeInputs(rangeEl)
    if (!inputs) return
    var startVal = String(inputs.startInput.value || '').trim()
    var endVal = String(inputs.endInput.value || '').trim()
    if (startVal !== expectStart) {
      setInputValue(inputs.startInput, expectStart)
    }
    if (endVal !== expectEnd) {
      setInputValue(inputs.endInput, expectEnd)
    }
  }

  ;[0, 50, 150, 400, 1200].forEach(function (ms) {
    setTimeout(repairIfNeeded, ms)
  })

  rangeEl.addEventListener(
    'focusin',
    function luxRangeMemoryFocusRepair() {
      repairIfNeeded()
    },
    true
  )
}

function isInsideSachkonten(rangeEl) {
  return rangeEl.closest && rangeEl.closest('.Page.sachkonten')
}

function getCalendarYearRange(year) {
  var y = String(year)
  return { startDate: '01.01.' + y, endDate: '31.12.' + y }
}

function getCalendarYearRangeIso(year) {
  var y = String(year)
  return { startDate: y + '-01-01', endDate: y + '-12-31' }
}

function navigateWithDateRange(startDate, endDate) {
  var url = new URL(window.location.href)
  url.searchParams.set('startDate', startDate)
  url.searchParams.set('endDate', endDate)
  window.location.href = url.toString()
}

function extractAccountBalancesPropertyId() {
  var match = window.location.pathname.match(/^\/account-balances\/([^/?]+)/)
  return match ? match[1] : null
}

function fetchPropertyEconomicYear(propertyHrId) {
  var auth = null
  try {
    auth = JSON.parse(localStorage.getItem('auth') || '{}')
  } catch (e) {
    return Promise.resolve(null)
  }
  var token = auth && auth.token
  if (!token) return Promise.resolve(null)
  var origin = window.location.origin
  var apiUrl = origin + '/services/pmp-accounting/api/v1/properties/' + encodeURIComponent(propertyHrId)
  return fetch(apiUrl, {
    method: 'GET',
    headers: { Authorization: 'Bearer ' + token },
  })
    .then(function (res) {
      if (!res.ok) return null
      return res.json()
    })
    .then(function (data) {
      if (!data || data.economicYearStart == null || data.economicYearEnd == null) return null
      var start = data.economicYearStart
      var end = data.economicYearEnd
      if (typeof start !== 'string' || typeof end !== 'string') return null
      return { economicYearStart: start, economicYearEnd: end }
    })
    .catch(function () {
      return null
    })
}

function parseMonthDayFromIso(isoStr) {
  if (typeof isoStr !== 'string') return null
  var parts = isoStr.split(/[-T]/)
  if (parts.length < 2) return null
  var month = parseInt(parts[1], 10)
  var day = parts[2] ? parseInt(parts[2], 10) : 1
  if (isNaN(month) || month < 1 || month > 12) return null
  if (isNaN(day) || day < 1) day = 1
  var mm = String(month).padStart(2, '0')
  var dd = String(Math.min(day, 31)).padStart(2, '0')
  return { month: mm, day: dd }
}

function getClientCurrentYear() {
  return new Date().getFullYear()
}

function monthDayBeforeOrEqual(m1, d1, m2, d2) {
  if (m1 !== m2) return m1 < m2
  return d1 <= d2
}

function buildEconomicYearRangeForYear(startMonthDay, endMonthDay, year) {
  if (!startMonthDay || !endMonthDay) return null
  var startDate = year + '-' + startMonthDay.month + '-' + startMonthDay.day
  var endYear = year
  var startMonthNum = parseInt(startMonthDay.month, 10)
  var endMonthNum = parseInt(endMonthDay.month, 10)
  var endDayNum = parseInt(endMonthDay.day, 10)
  var startDayNum = parseInt(startMonthDay.day, 10)
  if (endMonthNum < startMonthNum || (endMonthNum === startMonthNum && endDayNum < startDayNum)) {
    endYear = year + 1
  }
  var endDate = endYear + '-' + endMonthDay.month + '-' + endMonthDay.day
  return { startDate: startDate, endDate: endDate }
}

function getStartYearOfEconomicYearContainingToday(startMonthDay, endMonthDay) {
  var now = new Date()
  var currentYear = now.getFullYear()
  var todayMonth = now.getMonth() + 1
  var todayDay = now.getDate()
  var startM = parseInt(startMonthDay.month, 10)
  var startD = parseInt(startMonthDay.day, 10)
  var endM = parseInt(endMonthDay.month, 10)
  var endD = parseInt(endMonthDay.day, 10)
  var spansTwoYears = endM < startM || (endM === startM && endD < startD)
  if (!spansTwoYears) {
    return currentYear
  }
  if (monthDayBeforeOrEqual(todayMonth, todayDay, endM, endD)) {
    return currentYear - 1
  }
  return currentYear
}

function economicYearRangesFromApi(economicYearStart, economicYearEnd, currentYearOverride) {
  var startMonthDay = parseMonthDayFromIso(economicYearStart)
  var endMonthDay = parseMonthDayFromIso(economicYearEnd)
  if (!startMonthDay || !endMonthDay) return null
  var currentYear = currentYearOverride !== undefined && currentYearOverride !== null
    ? Number(currentYearOverride)
    : getClientCurrentYear()
  if (isNaN(currentYear)) currentYear = getClientCurrentYear()
  var startYearCurrent = getStartYearOfEconomicYearContainingToday(startMonthDay, endMonthDay)
  var current = buildEconomicYearRangeForYear(startMonthDay, endMonthDay, startYearCurrent)
  var last = buildEconomicYearRangeForYear(startMonthDay, endMonthDay, startYearCurrent - 1)
  return current && last ? { current: current, last: last } : null
}

function createLockIcon(isSaved) {
  const button = document.createElement('button')
  button.type = 'button'
  button.setAttribute('aria-label', isSaved ? 'Gespeichertes Datum verwenden (bereits gespeichert)' : 'Datumsbereich speichern')
  button.className = 'lux-range-memory-lock'
  button.style.cssText = [
    'display:inline-flex',
    'align-items:center',
    'justify-content:center',
    'width:40px',
    'height:42px',
    'padding:0',
    'border:.2rem solid #eee',
    'margin-left:-0.2rem',
    'border-radius:4px',
    'background:#fff',
    'cursor:pointer',
    'flex-shrink:0',
    'vertical-align:middle',
    LUX_RANGE_FONT_STYLE,
  ].join(';')
  button.innerHTML = isSaved
    ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>'
    : '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>'
  return button
}

function createSachkontenYearDropdown(rangeEl) {
  var now = new Date()
  var currentYear = now.getFullYear()
  var lastYear = currentYear - 1

  var container = document.createElement('div')
  container.className = 'lux-range-year-dropdown'
  container.style.cssText = 'position:relative; margin-left:8px; flex-shrink:0;'

  var trigger = document.createElement('button')
  trigger.type = 'button'
  trigger.setAttribute('aria-label', 'Zeitraum wählen')
  trigger.className = 'lux-range-year-dropdown-trigger'
  trigger.style.cssText = [
    'display:inline-flex',
    'align-items:center',
    'justify-content:center',
    'height:42px',
    'padding:0 10px',
    'border:.2rem solid #eee',
    'border-radius:4px',
    'background:#fff',
    'cursor:pointer',
    'color:#333',
    'white-space:nowrap',
    LUX_RANGE_FONT_STYLE,
  ].join(';')
  trigger.innerHTML = 'Zeitraum <svg width="10" height="10" viewBox="0 0 10 5" style="margin-left:4px;"><path fill="currentColor" d="M0 0L5 5L10 0H0Z"/></svg>'

  var panel = document.createElement('div')
  panel.className = 'lux-range-year-dropdown-panel'
  panel.style.cssText = [
    'display:none',
    'position:absolute',
    'top:100%',
    'left:0',
    'margin-top:4px',
    'width:max-content',
    'min-width:100%',
    'background:#fff',
    'border:1px solid #d9d9d9',
    'border-radius:4px',
    'box-shadow:0 2px 8px rgba(0,0,0,0.15)',
    'z-index:1050',
    'padding:4px 0',
  ].join(';')

  function addOption(label, startDate, endDate) {
    var item = document.createElement('button')
    item.type = 'button'
    item.className = 'lux-range-year-dropdown-item'
    item.style.cssText = [
      'display:block',
      'width:100%',
      'text-align:left',
      'padding:8px 12px',
      'border:none',
      'background:transparent',
      'cursor:pointer',
      'color:#333',
      'white-space:nowrap',
      LUX_RANGE_FONT_STYLE,
    ].join(';')
    item.addEventListener('mouseenter', function () { item.style.backgroundColor = '#f5f5f5' })
    item.addEventListener('mouseleave', function () { item.style.backgroundColor = 'transparent' })
    item.textContent = label
    item.addEventListener('click', function () {
      panel.style.display = 'none'
      document.removeEventListener('click', closePanel)
      navigateWithDateRange(startDate, endDate)
    })
    panel.appendChild(item)
  }

  addOption('Letztes Kalenderjahr', getCalendarYearRangeIso(lastYear).startDate, getCalendarYearRangeIso(lastYear).endDate)
  addOption('Aktuelles Kalenderjahr', getCalendarYearRangeIso(currentYear).startDate, getCalendarYearRangeIso(currentYear).endDate)

  var propertyHrId = extractAccountBalancesPropertyId()
  if (propertyHrId) {
    fetchPropertyEconomicYear(propertyHrId).then(function (economic) {
      if (!economic) return
      var yearNow = getClientCurrentYear()
      var ranges = economicYearRangesFromApi(economic.economicYearStart, economic.economicYearEnd, yearNow)
      if (!ranges) return
      addOption('Letztes Wirtschaftsjahr', ranges.last.startDate, ranges.last.endDate)
      addOption('Aktuelles Wirtschaftsjahr', ranges.current.startDate, ranges.current.endDate)
    })
  }

  function closePanel(e) {
    if (e && container.contains(e.target)) return
    panel.style.display = 'none'
    document.removeEventListener('click', closePanel)
  }

  trigger.addEventListener('click', function (e) {
    e.stopPropagation()
    var open = panel.style.display === 'block'
    panel.style.display = open ? 'none' : 'block'
    if (!open) {
      setTimeout(function () {
        document.addEventListener('click', closePanel)
      }, 0)
    } else {
      document.removeEventListener('click', closePanel)
    }
  })

  container.appendChild(trigger)
  container.appendChild(panel)
  return container
}

function injectLockAndApplyMemory(rangeEl) {
  if (rangeEl.getAttribute(LOCK_MARKER_ATTR) === 'true') return

  const inputs = getRangeInputs(rangeEl)
  if (!inputs) return

  rangeEl.setAttribute(LOCK_MARKER_ATTR, 'true')

  applyStoredRangeToElement(rangeEl)
  if (getStoredRange()) {
    scheduleStoredRangeRepair(rangeEl)
  }

  const lockBtn = createLockIcon(!!getStoredRange())

  function setLockBtnState(isLocked) {
    lockBtn.innerHTML = isLocked
      ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>'
      : '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>'
    lockBtn.setAttribute('aria-label', isLocked ? 'Gespeichertes Datum verwenden (bereits gespeichert)' : 'Datumsbereich speichern')
  }

  lockBtn.addEventListener('click', function () {
    const current = getStoredRange()
    if (current) {
      clearStoredRange()
      setLockBtnState(false)
      window.dispatchEvent(new CustomEvent('lux-range-memory-changed'))
      return
    }
    const inputs = getRangeInputs(rangeEl)
    if (!inputs) return
    const startDate = (inputs.startInput && inputs.startInput.value) ? inputs.startInput.value.trim() : ''
    const endDate = (inputs.endInput && inputs.endInput.value) ? inputs.endInput.value.trim() : ''
    if (startDate && endDate) {
      setStoredRange(startDate, endDate)
      setLockBtnState(true)
      window.dispatchEvent(new CustomEvent('lux-range-memory-changed'))
    }
  })

  window.addEventListener('lux-range-memory-changed', function syncLockIcon() {
    setLockBtnState(!!getStoredRange())
  })

  if (rangeEl.parentNode) {
    const wrapper = document.createElement('div')
    wrapper.className = 'lux-range-input-memory-wrapper'
    wrapper.style.cssText = 'display:inline-flex; align-items:center;'
    rangeEl.parentNode.insertBefore(wrapper, rangeEl)
    wrapper.appendChild(rangeEl)
    wrapper.appendChild(lockBtn)
    if (isInsideSachkonten(rangeEl)) {
      wrapper.appendChild(createSachkontenYearDropdown(rangeEl))
    }
  }
}

function isPropertiesRoute() {
  return typeof window !== 'undefined' && window.location.pathname.indexOf('/properties/') !== -1
}

function isInvoiceRoute() {
  return typeof window !== 'undefined' && window.location.pathname.indexOf('/invoice/') !== -1
}

function attachRangeInputMemory() {
  if (isPropertiesRoute() || isInvoiceRoute()) return
  const list = document.querySelectorAll('.RangeInput')
  list.forEach(function (el) {
    injectLockAndApplyMemory(el)
  })
}

var rangeInputMemoryInitialized = false

function initRangeInputMemory() {
  if (isPropertiesRoute() || isInvoiceRoute()) return
  if (rangeInputMemoryInitialized) {
    attachRangeInputMemory()
    return
  }
  rangeInputMemoryInitialized = true
  attachRangeInputMemory()

  const observer = new MutationObserver(function () {
    attachRangeInputMemory()
  })

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true })
  } else {
    const bodyObserver = new MutationObserver(function () {
      if (document.body) {
        observer.observe(document.body, { childList: true, subtree: true })
        bodyObserver.disconnect()
      }
    })
    bodyObserver.observe(document.documentElement, { childList: true, subtree: true })
  }
}

if (typeof window !== 'undefined') {
  window.initRangeInputMemory = initRangeInputMemory
}
